/**
 * Settings types and defaults — extracted from ``hooks/useSettings.ts``
 * to break the circular import between the hook (which imports the
 * SettingsService class) and the service (which imports SettingsState +
 * DEFAULT_SETTINGS for its method signatures and fallback values).
 *
 * Vite/Rollup tolerated the cycle in dev (single-bundle module graph),
 * but in the production code-splitting chunk-graph the load order made
 * one of the imported symbols ``undefined`` at module init time, which
 * surfaced in production as ``s is not a function`` inside the minified
 * settings-service chunk.
 *
 * Convention: anything that's pure data (types + defaults) lives here;
 * the hook + service in their respective files only import from this
 * module, never from each other directly via the hook re-exports.
 *
 * The hook continues to re-export these for back-compat — existing
 * importers do not need to change their import paths.
 */

import { THEME_PRESETS } from '@/constants/theme-options'
import type { WidgetConfig } from '@/types/widget'
import type { Communicator2KnowledgeScope } from '@/components/applications/communicator2/lib/sourceScope'

type ImageMode = 'cover' | 'contain' | 'repeat'

export interface BackgroundSettings {
  image: string | null
  imageMode: ImageMode
}

export interface DesktopIconSettings {
  showIcons: boolean
  showLabels: boolean
}

export interface IconPosition {
  row: number
  col: number
}

export type IconPositions = Record<string, IconPosition>

export interface DesktopFolder {
  id: string
  name: string
  appIds: string[]
  position: IconPosition
  color?: string
  createdAt: number
}

export interface TaskbarSettings {
  location: 'top' | 'bottom' | 'left' | 'right'
  color: string
  displayMode: 'icon' | 'name' | 'both'
  textColor: string
}

export interface ChangelogSettings {
  /** `publishedAt` (ISO UTC) of the newest entry the person has seen (#1355). */
  lastSeenAt?: string
  /** Marker of the version-based changelog before #1355; no longer read, left in place for a rollback. */
  lastSeenVersion?: string
  hideOnStartup: boolean
}

export interface PersistedWindowState {
  appId: string
  subtitle?: string
  minimized: boolean
  isMaximized?: boolean
  position: { x: number; y: number }
  size: { w: number; h: number }
  snapSide: 'left' | 'right' | null
  params?: Record<string, string | number | boolean>
}

export interface SettingsState {
  background: BackgroundSettings
  darkMode: boolean
  theme_id: string | null  // UUID of selected theme
  language: string
  desktopIcons: DesktopIconSettings
  taskbar: TaskbarSettings
  driveOnlyMode: {
    enabled: boolean
    showAppsInSidebar: boolean
    allowDirectAppOpening: boolean
  }
  desktopIconPositions?: IconPositions
  desktopInstalledApps?: string[]  // Array of app IDs installed on desktop
  desktopFolders?: Record<string, DesktopFolder>  // Ordner auf dem Desktop
  widgetConfigs?: WidgetConfig[]
  changelog: ChangelogSettings
  windowConfigurations?: PersistedWindowState[]  // Offene Fenster für Restore nach Reload
  /**
   * Communicator 2.0: the user's preferred LLM — a registry id from
   * communicator2/lib/models (the 'auto' sentinel, an OpenAI tier, or a Gemini
   * alias). Sent as ChatRequest.model each turn. Undefined → registry default.
   */
  preferred_model?: string
  /**
   * Communicator 2.0: the user's preferred image model — a registry id from
   * communicator2/lib/image-models ('nano-banana' | 'gpt-image'). Sent as
   * ChatRequest.image_model each turn; overrides the create_image/edit_image
   * model server-side (cdb_tqs#302). Undefined → registry default (Nano Banana 2).
   */
  preferred_image_model?: string
  /**
   * Communicator 2.0: auto-ingest uploaded chat files into the user's personal
   * memory (Honcho, CDB-independent — cdb_tqs#536). Undefined → treated as true.
   */
  memory_auto_ingest?: boolean
  /**
   * Communicator 2.0: preferred answer language. 'de'/'en' hard-force the
   * response language; 'auto' (default) keeps per-message detection.
   */
  response_language?: 'auto' | 'de' | 'en'
  /**
   * Communicator 2.0: the persisted knowledge scope — "Alle Quellen", source
   * pills, Knowledge Areas, web search depth (#909). Written as one object;
   * read through `parseKnowledgeScope`, never trusted as typed.
   */
  communicator2_scope?: Communicator2KnowledgeScope
  /**
   * Notification Center (#1095): per category, whether its notifications show
   * in the panel (and count towards the unread badge) and whether they raise a
   * toast. Missing categories or channels mean "on". Read through
   * `parseNotificationPreferences`, never trusted as typed.
   */
  notification_preferences?: Partial<Record<string, { inApp?: boolean; toast?: boolean }>>
  /**
   * Choices a person repeats from one visit to the next, such as the knowledge
   * area a recording was last filed into (#1659). Read through
   * `parseRecentChoices`, never trusted as typed.
   */
  recent_choices?: unknown
  /**
   * cdbHARNESS (#1366, v0.2): the person's agents and teams. Read through
   * `parseHarnessStore`, never trusted as typed.
   */
  harness?: unknown
}

export const DEFAULT_SETTINGS: SettingsState = {
  background: {
    image: THEME_PRESETS.light.backgroundImage,
    imageMode: 'cover',
  },
  darkMode: false,
  theme_id: null,  // Will be set to client's default theme on first load
  language: 'de',
  desktopIcons: {
    showIcons: true,
    showLabels: true,
  },
  driveOnlyMode: {
    enabled: true,
    showAppsInSidebar: false,
    allowDirectAppOpening: false,
  },
  taskbar: {
    location: 'bottom',
    color: 'rgba(245, 245, 245, 1)',
    displayMode: 'both',
    textColor: 'rgba(20, 20, 20, 0.9)',
  },
  desktopIconPositions: {},
  desktopInstalledApps: ['app-store', 'settings', 'drive2', 'communicator2'],
  desktopFolders: {},
  changelog: {
    hideOnStartup: false,
  },
  memory_auto_ingest: true,
  response_language: 'auto',
}
